import {
  INDEX_DB_SESSION_KEYS,
  INDEX_DB_SESSION_KEYS_ATTRIBUTES_TABLE,
  INDEX_DB_SESSION_KEYS_HASHES_TABLE,
  INDEX_DB_SESSION_KEYS_VERSION,
} from "core/consts";
import { callIndexedDB, initIndexedDB } from "core/model/utils/IndexedDB";

type AttributesToHashes = { [attributeKey: string]: number };
type HashesToKeys = { [hash: number]: CryptoKey };

function generateUnsecureHash(content: string) {
  let hash = 0;
  if (content.length == 0) {
    return hash;
  }
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

/*
.  _  _   _   ___ _  _    ___   _   ___ _  _ ___
. | || | /_\ / __| || |  / __| /_\ / __| || | __|
. | __ |/ _ \\__ \ __ | | (__ / _ \ (__| __ | _|
. |_||_/_/ \_\___/_||_|  \___/_/ \_\___|_||_|___|
.
 */

// SIEVE eviction algorithm (NSDI'24): simpler than LRU with better hit rates.
// Uses one ordered map + a "hand" pointer that scans from tail to head.
// Visited entries get a second chance; unvisited entries are evicted first.
//
// Keys are the raw encrypted content strings — NOT a hash of them. Using a
// 32-bit hash as a key risks silent collisions: two different encrypted values
// mapping to the same key would return the wrong patient's decrypted data.
// The extra memory cost of string keys is worthwhile for correctness.
const HASH_CACHE_CAPACITY = 1000;
type SieveEntry = { value: string; visited: boolean };
const sieveItems: Map<string, SieveEntry> = new Map();
// Hand scans tail→head (index 0=oldest → n-1=newest). After eviction it
// steps back toward the tail so the next scan resumes just before the gap.
let sieveHand: string | null = null;

function sieveGet(key: string): string | undefined {
  const entry = sieveItems.get(key);
  if (!entry) return undefined;
  entry.visited = true;
  return entry.value;
}

function sieveSet(key: string, value: string): void {
  const existing = sieveItems.get(key);
  if (existing) {
    existing.value = value;
    return;
  }
  if (sieveItems.size >= HASH_CACHE_CAPACITY) {
    sieveEvict();
  }
  sieveItems.set(key, { value, visited: false });
}

function sieveEvict(): void {
  const keys = [...sieveItems.keys()]; // index 0 = tail (oldest), n-1 = head (newest)
  let idx = sieveHand !== null ? keys.indexOf(sieveHand) : 0;
  if (idx === -1) idx = 0;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[idx];
    const entry = sieveItems.get(key)!;
    if (!entry.visited) {
      sieveItems.delete(key);
      // Step hand back toward tail — next scan resumes from the predecessor
      sieveHand = idx > 0 ? keys[idx - 1] : null;
      return;
    }
    entry.visited = false;
    idx = (idx + 1) % keys.length;
  }
  // All entries were visited — evict the oldest as fallback
  sieveItems.delete(keys[0]);
  sieveHand = null;
}

function initHashCache() {
  // Flush any pending debounced writes before the page unloads so entries
  // added in the last 100ms aren't silently lost on tab close or SPA navigation.
  window.addEventListener("beforeunload", flushPersistHashCache);

  try {
    const raw = window.localStorage.getItem("hashCache");
    if (raw == null) return;

    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return;

    // Discard the old format where keys were 32-bit integer hashes —
    // those could silently return wrong patient data on collision.
    const isLegacyFormat =
      parsed.length > 0 && typeof parsed[0]?.[0] === "number";
    if (isLegacyFormat) {
      window.localStorage.removeItem("hashCache");
      return;
    }

    // Only load the most recent HASH_CACHE_CAPACITY entries so a bloated
    // cache from before this fix can't bypass the in-memory cap.
    const entries = parsed as [string, string][];
    const start = Math.max(0, entries.length - HASH_CACHE_CAPACITY);
    for (let i = start; i < entries.length; i++) {
      const [k, v] = entries[i];
      if (typeof k === "string" && typeof v === "string") {
        sieveItems.set(k, { value: v, visited: false });
      }
    }
  } catch (hashCacheError) {
    console.error("Failed to init hash cache: ", hashCacheError);
  }
}

export function getHashCache(encryptedContent: string): string | undefined {
  return sieveGet(encryptedContent);
}

// Debounced: batches all writes from a single render cycle into one
// localStorage write instead of one per decrypted field.
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersistHashCache() {
  if (persistTimer !== null) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    flushPersistHashCache();
  }, 100);
}

function flushPersistHashCache() {
  try {
    const entries = [...sieveItems.entries()].map(([k, e]) => [k, e.value]);
    window.localStorage.setItem("hashCache", JSON.stringify(entries));
  } catch (_quotaError) {
    // localStorage is nearly full from other data — clear our key and retry once.
    // If it still fails, skip persistence for this session; the in-memory
    // cache remains intact so decryption still works without a page reload.
    try {
      window.localStorage.removeItem("hashCache");
      const entries = [...sieveItems.entries()].map(([k, e]) => [k, e.value]);
      window.localStorage.setItem("hashCache", JSON.stringify(entries));
    } catch (retryError) {
      console.log(
        "Hash cache: localStorage full, skipping persistence",
        retryError,
      );
    }
  }
}

export function setHashCache(
  encryptedContent: string,
  decryptedContent: string,
) {
  try {
    if (decryptedContent != null) {
      sieveSet(encryptedContent, decryptedContent);
      schedulePersistHashCache();
    }
  } catch (setHashCacheError) {
    console.log("Failed to set hash cache: ", setHashCacheError);
  }
}

function clearHashCache() {
  try {
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    window.removeEventListener("beforeunload", flushPersistHashCache);
    sieveItems.clear();
    sieveHand = null;
    window.localStorage.removeItem("hashCache");
  } catch (clearHashCacheError) {
    console.log("Failed to clear hash cache: ", clearHashCacheError);
  }
}

/*
.                          _                                  .-.         
.                         :_;                                 : :         
.  .--.  .--.  .--.  .--. .-. .--. ,-.,-.   .--.  .--.   .--. : `-.  .--. 
. `._-.'' '_.'`._-.'`._-.': :' .; :: ,. :  '  ..'' .; ; '  ..': .. :' '_.'
. `.__.'`.__.'`.__.'`.__.':_;`.__.':_;:_;  `.__.'`.__,_;`.__.':_;:_;`.__.'
.
 */
const sessionCache: {
  attributesToHashes: AttributesToHashes;
  hashesToKeys: HashesToKeys;
} = { attributesToHashes: {}, hashesToKeys: {} };

function initSessionCache() {
  try {
    initIndexedDB(
      INDEX_DB_SESSION_KEYS,
      [
        INDEX_DB_SESSION_KEYS_ATTRIBUTES_TABLE,
        INDEX_DB_SESSION_KEYS_HASHES_TABLE,
      ],
      INDEX_DB_SESSION_KEYS_VERSION,
    );

    callIndexedDB(
      INDEX_DB_SESSION_KEYS,
      INDEX_DB_SESSION_KEYS_ATTRIBUTES_TABLE,
      INDEX_DB_SESSION_KEYS_VERSION,
      function (store: IDBObjectStore) {
        const getData = store.getAll();
        getData.onsuccess = function () {
          const result = getData.result;
          sessionCache.attributesToHashes = result.reduce(
            (
              acc: AttributesToHashes,
              cur: { encryptedSessionKeyHash: string; id: string },
            ) => ({
              ...acc,
              [cur.id]: cur.encryptedSessionKeyHash,
            }),
            {},
          );
          callIndexedDB(
            INDEX_DB_SESSION_KEYS,
            INDEX_DB_SESSION_KEYS_HASHES_TABLE,
            INDEX_DB_SESSION_KEYS_VERSION,
            function (store: IDBObjectStore) {
              const getData = store.getAll();
              getData.onsuccess = function () {
                const result = getData.result;
                sessionCache.hashesToKeys = result.reduce(
                  (
                    acc: HashesToKeys,
                    cur: { decryptedSessionKey: CryptoKey; id: number },
                  ) => ({
                    ...acc,
                    [cur.id]: cur.decryptedSessionKey,
                  }),
                  {},
                );
              };
            },
            "init",
          );
        };
      },
      "init",
    );
  } catch (sessionCacheError) {
    console.error("Failed to init session cache: ", sessionCacheError);
  }
}

export function getSessionCache(
  attributeKey: string,
  encryptedSessionKey?: string,
) {
  const oldEcryptedSessionKeyHash =
    sessionCache.attributesToHashes[attributeKey];

  if (!oldEcryptedSessionKeyHash) return undefined;

  if (!encryptedSessionKey)
    return sessionCache.hashesToKeys[oldEcryptedSessionKeyHash];

  const newEcryptedSessionKeyHash = generateUnsecureHash(encryptedSessionKey);
  if (oldEcryptedSessionKeyHash === newEcryptedSessionKeyHash)
    return sessionCache.hashesToKeys[oldEcryptedSessionKeyHash];

  return undefined;
}

export function setSessionCache(
  attributeKey: string,
  encryptedSessionKey: string,
  decryptedSessionKey: CryptoKey,
) {
  try {
    const encryptedSessionKeyHash = generateUnsecureHash(encryptedSessionKey);

    callIndexedDB(
      INDEX_DB_SESSION_KEYS,
      INDEX_DB_SESSION_KEYS_ATTRIBUTES_TABLE,
      INDEX_DB_SESSION_KEYS_VERSION,
      function (store: IDBObjectStore) {
        store.put({ encryptedSessionKeyHash, id: attributeKey });
      },
      "set",
    );
    sessionCache.attributesToHashes[attributeKey] = encryptedSessionKeyHash;

    callIndexedDB(
      INDEX_DB_SESSION_KEYS,
      INDEX_DB_SESSION_KEYS_HASHES_TABLE,
      INDEX_DB_SESSION_KEYS_VERSION,
      function (store: IDBObjectStore) {
        store.put({ decryptedSessionKey, id: encryptedSessionKeyHash });
      },
      "set",
    );
    sessionCache.hashesToKeys[encryptedSessionKeyHash] = decryptedSessionKey;
  } catch (setSessionKeyInCacheError) {
    console.log("Failed to set session cache: ", setSessionKeyInCacheError);
  }
}

function clearSessionCache() {
  try {
    callIndexedDB(
      INDEX_DB_SESSION_KEYS,
      INDEX_DB_SESSION_KEYS_ATTRIBUTES_TABLE,
      INDEX_DB_SESSION_KEYS_VERSION,
      function (store: IDBObjectStore) {
        store.clear();
      },
      "clear",
    );
    sessionCache.attributesToHashes = {};

    callIndexedDB(
      INDEX_DB_SESSION_KEYS,
      INDEX_DB_SESSION_KEYS_HASHES_TABLE,
      INDEX_DB_SESSION_KEYS_VERSION,
      function (store: IDBObjectStore) {
        store.clear();
      },
      "clear",
    );
    sessionCache.hashesToKeys = {};
  } catch (clearSessionKeyInCacheError) {
    console.log("Failed to clear session cache: ", clearSessionKeyInCacheError);
  }
}

export function initEncryptionCaches() {
  initSessionCache();
  initHashCache();
}

export function clearEncryptionCaches() {
  clearHashCache();
  clearSessionCache();
}
